'use client';

import { WalletReadyState } from '@solana/wallet-adapter-base';
import { Wallet } from '@solana/wallet-adapter-react';
import { PhantomWalletName } from '@solana/wallet-adapter-wallets';
import { useMemo } from 'react';
import useWalletContext from 'src/hooks/useWalletContext';

const WALLETS_WITH_AUTOCONFIRM: string[] = [PhantomWalletName];

// the order of these wallets determines their priority in the UI
const PRIORITIZED_WALLETS = [
	'Mobile Wallet Adapter',
	PhantomWalletName,
	'Solflare',
	'Backpack',
];

export type WalletToDisplayInfo = {
	provider: Wallet;
	featuresAutoConfirm?: boolean;
	suggestedHighlight?: boolean;
	action?: () => void;
};

export const HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY =
	'historicallySelectedWallets';
export const getHistoricallySelectedWallets = () => {
	return JSON.parse(
		localStorage.getItem(HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY) || '[]'
	);
};

export const LAST_USED_WALLET_NAME_LOCALSTORAGE_KEY = 'lastUsedWalletName';

const useWalletsToDisplay = (numberOfWalletsToShow?: number) => {
	const walletContext = useWalletContext();

	const memoizedWallets = useMemo(() => {
		let walletsToShow: WalletToDisplayInfo[] = [];
		const hiddenWallets: WalletToDisplayInfo[] = [];

		if (walletContext?.wallets && walletContext?.wallets?.length > 0) {
			walletContext.wallets
				.slice()
				.sort((walletA, walletB) => {
					// Move phantom to the top of the list
					const prioritizedIndexA = PRIORITIZED_WALLETS.indexOf(
						walletA.adapter?.name
					);
					const prioritizedIndexB = PRIORITIZED_WALLETS.indexOf(
						walletB.adapter?.name
					);

					if (prioritizedIndexA !== -1 && prioritizedIndexB !== -1) {
						return prioritizedIndexA - prioritizedIndexB;
					}

					if (prioritizedIndexA !== -1) {
						return -1;
					}

					if (prioritizedIndexB !== -1) {
						return 1;
					}

					return 0;
				})
				.forEach((walletProvider) => {
					const walletDisplayInfo: WalletToDisplayInfo = {
						provider: walletProvider,
					};

					if (WALLETS_WITH_AUTOCONFIRM.includes(walletProvider.adapter.name)) {
						walletDisplayInfo.featuresAutoConfirm = true;
					}

					const walletInstalled =
						walletProvider.readyState === WalletReadyState.Installed ||
						walletProvider.readyState === WalletReadyState.Loadable; // this could include wallet adapters that are installed in the codebase (e.g. Solflare)

					// Solflare already auto-opens to an install page when not installed,
					// so we just handle this case for Phantom.
					if (
						!walletInstalled &&
						walletProvider.adapter.name === PhantomWalletName
					) {
						walletDisplayInfo.action = () =>
							window.open(
								'https://phantom.com/download',
								'_blank',
								'noopener,noreferrer'
							);
					}

					const shouldShowWallet =
						walletInstalled &&
						PRIORITIZED_WALLETS.includes(walletProvider.adapter.name);

					if (
						shouldShowWallet &&
						(numberOfWalletsToShow === undefined ||
							walletsToShow.length < numberOfWalletsToShow)
					) {
						walletsToShow.push(walletDisplayInfo);
						return;
					}

					hiddenWallets.push(walletDisplayInfo);
				});
		}

		// Swap the previously used wallet with the last wallet in the `walletsToShow` list.
		const lastUsedWalletName = localStorage.getItem(
			LAST_USED_WALLET_NAME_LOCALSTORAGE_KEY
		);
		if (
			lastUsedWalletName &&
			!walletsToShow.some(
				(wallet) => wallet.provider.adapter.name === lastUsedWalletName
			)
		) {
			const hiddenWalletIndex = hiddenWallets.findIndex(
				(wallet) => wallet.provider.adapter.name === lastUsedWalletName
			);
			const shownWalletIndex = walletsToShow.length - 1;

			if (hiddenWalletIndex !== -1 && shownWalletIndex !== -1) {
				const hiddenWallet = hiddenWallets[hiddenWalletIndex];
				hiddenWallets[hiddenWalletIndex] = walletsToShow[shownWalletIndex];
				walletsToShow[shownWalletIndex] = hiddenWallet;
			}
		}

		// we only show the first MetaMask wallet, because of legacy reasons - we used to integrate MetaMask snap wallets, but have since deprecated them.
		// however if the user does not remove the snap from their MM, it will show up on the list
		walletsToShow = walletsToShow.filter(
			(wallet, index) =>
				wallet.provider.adapter.name !== 'MetaMask' ||
				walletsToShow.findIndex(
					(w) => w.provider.adapter.name === 'MetaMask'
				) === index
		);

		const hiddenDetectedWallets = hiddenWallets.filter(
			(wallet) => wallet.provider.readyState === WalletReadyState.Installed
		);
		const hiddenNonDetectedWallets = hiddenWallets.filter(
			(wallet) => wallet.provider.readyState !== WalletReadyState.Installed
		);

		return {
			walletsToShow,
			hiddenWallets,
			hiddenDetectedWallets,
			hiddenNonDetectedWallets,
		};
	}, [numberOfWalletsToShow, walletContext.wallets]);

	return memoizedWallets;
};

export default useWalletsToDisplay;
